import React from "react";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  StarFilled,
  WarningOutlined,
} from "@ant-design/icons";

interface CVDetailsPageProps {
  candidateData?: any;
  onBack?: () => void;
}

const CVDetailsPage: React.FC<CVDetailsPageProps> = ({
  candidateData,
  onBack,
}) => {
  if (!candidateData) {
    return (
      <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center">
        <span className="text-sm text-gray-500">
          No candidate data available
        </span>
      </div>
    );
  }

  const parseField = (value: any): any => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }

    return value;
  };

  const raw = candidateData.rawData ?? candidateData ?? {};

  const name =
    candidateData.name ?? candidateData.talent ?? raw.name ?? "Candidate";

  const summary =
    candidateData.summary ??
    candidateData.jobSummary ??
    raw.summary ??
    "No summary available";

  const score =
    candidateData.score ??
    raw.score ??
    (candidateData.fit
      ? parseInt(String(candidateData.fit).replace("%", ""), 10)
      : 0);

  const experienceLevel =
    candidateData.experienceLevel ??
    candidateData.status ??
    raw.experienceLevel ??
    "";

  const filename = candidateData.filename ?? raw.filename ?? "";

  const email = candidateData.email ?? raw.email ?? "";

  const phone = candidateData.phone ?? raw.phone ?? "";

  const location =
    candidateData.location ??
    candidateData.address ??
    raw.location ??
    raw.address ??
    "";

  const linkedin =
    candidateData.linkedinUrl ??
    candidateData.linkedin ??
    raw.linkedinUrl ??
    raw.linkedin ??
    "";

  const education = parseField(raw.education ?? candidateData.education) ?? [];

  const workExperience =
    parseField(
      raw.workExperience ??
        raw.experience ??
        candidateData.workExperience ??
        candidateData.experience,
    ) ?? [];

  const skills = parseField(raw.skills ?? candidateData.skills) ?? [];

  const strengths = parseField(raw.strengths ?? candidateData.strengths) ?? [];

 const weaknesses =
   parseField(raw.weaknesses ?? candidateData.weaknesses) ?? [];

 const reviewFlags =
   parseField(raw.reviewFlags ?? candidateData.reviewFlags) ?? [];

 const achievements =
   parseField(raw.achievements ?? candidateData.achievements) ?? [];

 const factorScores =
   parseField(raw.factorScores ?? candidateData.factorScores) ?? {};

 const educationArray = Array.isArray(education) ? education : [];

 const workExperienceArray = Array.isArray(workExperience)
   ? workExperience
   : [];

 const skillsArray = Array.isArray(skills) ? skills : [];

 const strengthsArray = Array.isArray(strengths) ? strengths : [];

 const weaknessesArray = Array.isArray(weaknesses) ? weaknesses : [];

 const reviewFlagsArray = Array.isArray(reviewFlags) ? reviewFlags : [];
 const achievementsArray = Array.isArray(achievements) ? achievements : [];

  const role =
    workExperienceArray?.[0]?.title ??
    workExperienceArray?.[0]?.role ??
    raw.jobTitle ??
    candidateData.jobTitle ??
    "";

  const getFactorPercentage = (value: string | undefined): number => {
    if (!value || typeof value !== "string") {
      return 0;
    }

    const [earned, total] = value.split("/").map(Number);

    if (Number.isNaN(earned) || Number.isNaN(total) || total === 0) {
      return 0;
    }

    return Math.round((earned / total) * 100);
  };

  const skillsScore = getFactorPercentage(factorScores.skills_match);

  const experienceScore = getFactorPercentage(factorScores.experience_depth);

  const educationScore = getFactorPercentage(factorScores.education);

  const keywordsScore = getFactorPercentage(factorScores.keywords);

  const overallScore = Number(score) || 0;

  type CandidateStatus =
    | "Strong Shortlist"
    | "Good Fit"
    | "Partial Fit"
    | "Weak Fit"
    | "Low Fit";

  const RECOMMENDATION_MAP: Record<
    CandidateStatus,
    { label: string; sentence: string }
  > = {
    "Strong Shortlist": {
      label: "Strongly Recommended",
      sentence:
        "The evidence supports prioritising the candidate for the next stage.",
    },
    "Good Fit": {
      label: "Recommended",
      sentence:
        "The evidence supports progressing the candidate to the next stage.",
    },
    "Partial Fit": {
      label: "Recommended",
      sentence:
        "The evidence supports considering the candidate for the next stage.",
    },
    "Weak Fit": {
      label: "Not Recommended for this role",
      sentence:
        "The evidence does not support progressing the candidate for this role.",
    },
    "Low Fit": {
      label: "Not Recommended for this role",
      sentence:
        "The evidence does not support progressing the candidate for this role.",
    },
  };

  const normalizeStatus = (value: any): CandidateStatus => {
    const raw = String(value ?? "").trim();
    const lower = raw.toLowerCase();

    if (lower === "strong shortlist") return "Strong Shortlist";
    if (lower === "good fit") return "Good Fit";
    if (lower === "partial fit") return "Partial Fit";
    if (lower === "weak fit") return "Weak Fit";
    if (lower === "low fit") return "Low Fit";
    return "Low Fit";
  };

  const candidateStatus: CandidateStatus = normalizeStatus(experienceLevel);
  const recommendation = RECOMMENDATION_MAP[candidateStatus];

  const isStrongFit = candidateStatus === "Strong Shortlist";
  const isMediumFit =
    candidateStatus === "Good Fit" || candidateStatus === "Partial Fit";

  const fitColor = isStrongFit
    ? "#52D600"
    : isMediumFit
      ? "#F59E0B"
      : "#EF4444";

  const fitBackground = isStrongFit
    ? "#EEFFE8"
    : isMediumFit
      ? "#FFF7E5"
      : "#FFF1F1";

  const recommendationTitle = recommendation.label;
  const recommendationText = recommendation.sentence;
  const scoreRows = [
    {
      label: "Skills",
      value: skillsScore,
    },
    {
      label: "Experience",
      value: experienceScore,
    },
    {
      label: "Education",
      value: educationScore,
    },
    {
      label: "Achievement",
      value: getFactorPercentage(factorScores.achievements),
    },
    {
      label: "Overall",
      value: overallScore,
      overall: true,
    },
  ];

  const getScoreBarColor = (value: number) => {
    if (value >= 70) {
      return "#52D600";
    }

    if (value >= 50) {
      return "#F5A623";
    }

    return "#EF4444";
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] text-[#202633]">
      <div className="w-full px-6 lg:px-8 py-6">
        <div className="bg-white border border-[#E6E8EE] rounded-[14px] px-5 py-4 mb-5">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="inline-flex items-center rounded-full border px-2 py-[2px] text-[10px] font-medium"
              style={{
                color: fitColor,
                borderColor: fitColor,
              }}
            >
              {overallScore}%
            </span>

            <span
              className="inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-medium"
              style={{
                color: fitColor,
                backgroundColor: fitBackground,
              }}
            >
              {experienceLevel || "Fit"}
            </span>
          </div>

          <h2 className="m-0 text-[18px] leading-6 font-semibold text-[#242B35]">
            {name}
          </h2>

          {role && (
            <p className="mt-1 mb-0 text-[13px] text-[#737B91]">{role}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-[12px] text-[#737B91]">
            {email && (
              <div className="flex items-center gap-2">
                <MailOutlined />
                <span>{email}</span>
              </div>
            )}

            {phone && (
              <div className="flex items-center gap-2">
                <PhoneOutlined />
                <span>{phone}</span>
              </div>
            )}

            {location && (
              <div className="flex items-center gap-2">
                <EnvironmentOutlined />
                <span>{location}</span>
              </div>
            )}

            {linkedin && (
              <a
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 !text-[#737B91] hover:!text-[#07166B]"
              >
                <LinkOutlined />
                <span>LinkedIn</span>
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)] gap-5 items-start">
          <div className="space-y-5">
            <div className="bg-white border border-[#E6E8EE] rounded-[14px] p-5">
              <h3 className="m-0 text-[15px] font-semibold text-[#252B34]">
                Fit Summary
              </h3>

              <p className="mt-4 mb-0 text-[12px] leading-[19px] text-[#747C91]">
                {summary}
              </p>

              <div className="mt-5">
                <h4 className="m-0 mb-3 text-[15px] font-semibold text-[#313742]">
                  Top Strengths
                </h4>

                {strengthsArray.length > 0 ? (
                  <div className="space-y-[9px]">
                    {strengthsArray.map((strength: string, index: number) => (
                      <div key={index} className="flex items-start gap-2">
                        <CheckCircleOutlined className="text-[#52D600] text-[12px] mt-[2px]" />

                        <span className="text-[12px] leading-[17px] text-[#747C91]">
                          {strength}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">
                    No strengths available
                  </span>
                )}
              </div>

              <div className="mt-5">
                <h4 className="m-0 mb-3 text-[15px] font-semibold text-[#313742]">
                  Potential Gaps
                </h4>

                {weaknessesArray.length > 0 ? (
                  <div className="space-y-[9px]">
                    {weaknessesArray.map((weakness: string, index: number) => (
                      <div key={index} className="flex items-start gap-2">
                        <WarningOutlined className="text-[#F5A623] text-[12px] mt-[2px]" />

                        <span className="text-[12px] leading-[17px] text-[#747C91]">
                          {weakness}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">
                    No potential gaps identified
                  </span>
                )}
              </div>

              <div className="mt-5">
                <h4 className="m-0 mb-3 text-[15px] font-semibold text-[#313742]">
                  Review Flags
                </h4>
                {reviewFlagsArray.length > 0 ? (
                  <div className="space-y-[9px]">
                    {reviewFlagsArray.map((flag: string, index: number) => (
                      <div key={index} className="flex items-start gap-2">
                        <WarningOutlined className="text-[#F5A623] text-[12px] mt-[2px]" />
                        <span className="text-[12px] leading-[17px] text-[#747C91]">
                          {flag}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">
                    No review flags raised
                  </span>
                )}
              </div>

              <div className="mt-5">
                <h4 className="m-0 mb-3 text-[15px] font-semibold text-[#313742]">
                  Achievements
                </h4>
                {achievementsArray.length > 0 ? (
                  <div className="space-y-[9px]">
                    {achievementsArray.map(
                      (achievement: string, index: number) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircleOutlined className="text-[#52D600] text-[12px] mt-[2px]" />
                          <span className="text-[12px] leading-[17px] text-[#747C91]">
                            {achievement}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">
                    No achievements recorded
                  </span>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#E6E8EE] rounded-[14px] p-5">
              <h3 className="m-0 text-[15px] font-semibold text-[#252B34]">
                Skills & Competencies
              </h3>

              <div className="mt-4 flex flex-wrap gap-2">
                {skillsArray.length > 0 ? (
                  skillsArray.map((skill: any, index: number) => {
                    const label =
                      typeof skill === "string"
                        ? skill
                        : (skill?.name ?? skill?.skill ?? String(skill));

                    return (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-full bg-[#ECECF8] px-3 py-[5px] text-[10px] font-medium text-[#07166B]"
                      >
                        {label}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">
                    No skills found
                  </span>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#E6E8EE] rounded-[14px] p-5">
              <h3 className="m-0 text-[15px] font-semibold text-[#252B34]">
                Education History
              </h3>

              <div className="mt-4 space-y-3">
                {educationArray.length > 0 ? (
                  educationArray.map((edu: any, index: number) => {
                    const degree = edu.degree || "Degree";
                    const school = edu.school ?? edu.institution ?? "";

                    const fieldOfStudyRaw =
                      edu.fieldOfStudy ?? edu.field_of_study ?? "";
                    const fieldOfStudy =
                      fieldOfStudyRaw && fieldOfStudyRaw !== "Not available"
                        ? String(fieldOfStudyRaw).trim()
                        : "";

                    const startDate = edu.startDate ?? edu.start_date ?? "";
                    const endDate =
                      edu.endDate ?? edu.end_date ?? edu.year ?? "";
                    const validStartDate =
                      startDate && startDate !== "Not available";
                    const validEndDate = endDate && endDate !== "Not available";

                    const dates = [
                      validStartDate ? startDate : "",
                      validEndDate ? endDate : "",
                    ]
                      .filter(Boolean)
                      .join(" - ");

                    return (
                      <div
                        key={index}
                        className="border border-[#EAEBEF] rounded-[8px] px-3 py-3 flex flex-col gap-1.5"
                      >
                        <div className="text-[13px] font-medium leading-[18px] text-[#252B34]">
                          <span className="mr-1 text-[#252B34]">•</span>
                          {degree}
                        </div>

                        {fieldOfStudy && (
                          <div className="text-[12px] leading-[18px] text-[#747C91] ml-3">
                            {fieldOfStudy}
                          </div>
                        )}

                        {school && (
                          <div className="text-[12px] leading-[18px] text-[#747C91] ml-3">
                            {school}
                          </div>
                        )}

                        {dates && (
                          <div className="text-[12px] leading-[18px] text-[#747C91] ml-3">
                            {dates}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">
                    No education information available
                  </span>
                )}
              </div>
            </div>

            {/* WORK EXPERIENCE */}

            <div className="bg-white border border-[#E6E8EE] rounded-[14px] p-5">
              <h3 className="m-0 text-[15px] font-semibold text-[#252B34]">
                Work Experience
              </h3>

              <div className="mt-4 space-y-3">
                {workExperienceArray.length > 0 ? (
                  workExperienceArray.map((exp: any, index: number) => {
                    const title = exp.title ?? exp.role ?? "Position";

                    const company = exp.company ?? "";

                    const startDate = exp.startDate ?? exp.start_date ?? "";

                    const endDate = exp.endDate ?? exp.end_date ?? "";

                    return (
                      <div
                        key={index}
                        className="border border-[#EAEBEF] rounded-[8px] px-3 py-3"
                      >
                        <div className="text-[12px] leading-[18px] text-[#252B34]">
                          <span className="mr-1">•</span>
                          {title}
                          {company && ` at ${company}`}
                        </div>

                        {(startDate || endDate) && (
                          <div className="mt-[2px] text-[12px] leading-[18px] text-[#252B34]">
                            {startDate}

                            {startDate && endDate ? " - " : ""}

                            {endDate}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span className="text-[12px] text-[#9CA3AF]">
                    No work experience available
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-white border border-[#E6E8EE] rounded-[14px] p-5">
              <h3 className="m-0 text-[15px] font-semibold text-[#252B34]">
                Fit Score
              </h3>

              <div className="flex flex-col items-center mt-4">
                <div
                  className="w-[104px] h-[104px] rounded-full p-[7px]"
                  style={{
                    background: `conic-gradient(
                      ${fitColor} ${Math.min(overallScore, 100) * 3.6}deg,
                      #ECEEF2 0deg
                    )`,
                  }}
                >
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                    <span className="text-[28px] font-medium text-[#252B34]">
                      {overallScore}%
                    </span>
                  </div>
                </div>

                <span
                  className="mt-4 inline-flex items-center rounded-full px-3 py-[4px] text-[10px] font-medium"
                  style={{
                    color: fitColor,
                    backgroundColor: fitBackground,
                  }}
                >
                  {experienceLevel || "Fit"}
                </span>
              </div>

              <div className="mt-6 space-y-3">
                {scoreRows.map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between items-center mb-[4px]">
                      <span className="text-[12px] text-[#838A9A]">
                        {item.label}
                      </span>

                      <span className="text-[12px] font-medium text-[#252B34]">
                        {item.value}%
                      </span>
                    </div>

                    <div className="h-[5px] bg-[#ECEEF2] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(item.value, 100)}%`,
                          backgroundColor: item.overall
                            ? "#07166B"
                            : getScoreBarColor(item.value),
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#E6E8EE] rounded-[14px] p-5">
              <h3 className="m-0 text-[15px] font-semibold text-[#252B34]">
                Recommendation
              </h3>

              <div
                className="mt-4 rounded-[10px] p-4 border"
                style={{
                  backgroundColor: isStrongFit
                    ? "#EAF8EF"
                    : isMediumFit
                      ? "#FFF7E8"
                      : "#FFF2F2",
                  borderColor: isStrongFit
                    ? "#BEE8CA"
                    : isMediumFit
                      ? "#F5D69B"
                      : "#F4C6C6",
                }}
              >
                <div className="flex items-center gap-2">
                  <StarFilled className="text-[#07166B] text-[13px]" />

                  <span className="text-[13px] font-semibold text-[#07166B]">
                    {recommendationTitle}
                  </span>
                </div>

                <p className="mt-2 mb-4 text-[12px] leading-[17px] text-[#747C91]">
                  {recommendationText}
                </p>

                {/* {isStrongFit && (
                  <button
                    type="button"
                    className="w-full h-[36px] rounded-[8px] bg-[#07166B] text-white text-[11px] font-medium hover:opacity-90"
                  >
                    Move to Shortlist
                  </button>
                )} */}
              </div>

              {/* <button
                type="button"
                className="mt-4 w-full h-[36px] rounded-[8px] border border-[#E2E5EB] bg-white text-[11px] font-medium text-[#303641] hover:bg-gray-50"
              >
                Add to Talent Pool
              </button> */}

              {filename ? (
                <a
                  href={filename}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-3"
                >
                  <button
                    type="button"
                    className="w-full h-[36px] rounded-[8px] border border-[#E2E5EB] bg-white text-[11px] font-medium text-[#303641] hover:bg-gray-50 flex items-center justify-center gap-2"
                  >
                    <DownloadOutlined />
                    Download
                  </button>
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="mt-3 w-full h-[36px] rounded-[8px] border border-[#E2E5EB] bg-white text-[11px] font-medium text-[#A5A9B3] cursor-not-allowed"
                >
                  Download
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVDetailsPage;
